import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Tabs, Tab, Badge, Alert, Table, Spinner, Button, ProgressBar, ListGroup } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { devLog, devError } from '../utils/devLogger';
import ExternalNavbar from '../components/ExternalNavbar';
import ProgressReportGenerator from '../components/ProgressReportGenerator';
import TakeALotLeaderboard from '../components/TakeALotLeaderboard';
import '../styles/brand.css';

const normalizeCohortName = (name) => (name || '').toLowerCase().replace(/\s+/g, ' ').trim();
const normalizeCohortMatch = (name) =>
  normalizeCohortName(name)
    .replace(/\btraining\b/g, '')
    .replace(/\s+-\s+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const ExternalViewerDashboard = () => {
  const { currentUser, userProfile, isExternalViewer } = useAuth();
  const [assignedPrograms, setAssignedPrograms] = useState([]);
  const [allowedCohorts, setAllowedCohorts] = useState([]);
  const [cohortNameMap, setCohortNameMap] = useState({});
  const [learners, setLearners] = useState([]);
  const [resources, setResources] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Load data effect (must be before any conditional returns)
  useEffect(() => {
    if (userProfile) {
      if (userProfile.assignedPrograms || userProfile.assignedCohorts) {
        loadData();
      } else {
        // No programs assigned yet, stop loading
        setLoading(false);
      }
    }
  }, [userProfile]);

  // Access guard (after hooks)
  if (!isExternalViewer || !isExternalViewer()) {
    return (
      <div className="container mt-5">
        <Alert variant="danger">
          <strong>Access Denied</strong>
          <p className="mb-0">This page is for external viewers only.</p>
        </Alert>
      </div>
    );
  }

  const loadData = async () => {
    setLoading(true);
    try {
      devLog('=== EXTERNAL VIEWER LOAD DATA START ===');
      devLog('User profile:', userProfile);
      devLog('Assigned cohorts:', userProfile.assignedCohorts);
      devLog('Assigned programs:', userProfile.assignedPrograms);

      const cohortsRef = collection(db, 'cohorts');
      const assignedCohortIds = userProfile.assignedCohorts || userProfile.managedCohorts || [];
      devLog('Will query cohort IDs:', assignedCohortIds);

      const loadCohortsByIds = async (cohortIds) => {
        if (cohortIds.length === 0) {
          devLog('No cohort IDs to query');
          return [];
        }
        const chunks = [];
        for (let i = 0; i < cohortIds.length; i += 10) {
          chunks.push(cohortIds.slice(i, i + 10));
        }

        const results = [];
        for (const chunk of chunks) {
          devLog('Querying cohorts by ID chunk:', chunk);
          try {
            const cohortQuery = query(cohortsRef, where('__name__', 'in', chunk));
            const snapshot = await getDocs(cohortQuery);
            devLog('Cohorts by ID query success, found:', snapshot.docs.length);
            results.push(...snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          } catch (err) {
            devError('ERROR querying cohorts by ID:', err);
            throw err;
          }
        }
        return results;
      };

      const loadCohortsByPrograms = async (programIds) => {
        if (programIds.length === 0) {
          devLog('No program IDs to query cohorts by');
          return [];
        }
        const chunks = [];
        for (let i = 0; i < programIds.length; i += 10) {
          chunks.push(programIds.slice(i, i + 10));
        }

        const results = [];
        for (const chunk of chunks) {
          devLog('Querying cohorts by programId chunk:', chunk);
          try {
            const cohortQuery = query(cohortsRef, where('programId', 'in', chunk));
            const snapshot = await getDocs(cohortQuery);
            devLog('Cohorts by programId query success, found:', snapshot.docs.length);
            results.push(...snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          } catch (err) {
            devError('ERROR querying cohorts by programId:', err);
            throw err;
          }
        }
        return results;
      };

      const loadCohortsByNames = async (names) => {
        if (names.length === 0) return [];
        const chunks = [];
        for (let i = 0; i < names.length; i += 10) {
          chunks.push(names.slice(i, i + 10));
        }

        const results = [];
        for (const chunk of chunks) {
          try {
            const byNameQuery = query(cohortsRef, where('name', 'in', chunk));
            const nameSnapshot = await getDocs(byNameQuery);
            results.push(...nameSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          } catch (err) {
            devError('ERROR querying cohorts by name:', err);
          }

          try {
            const byAltNameQuery = query(cohortsRef, where('cohortName', 'in', chunk));
            const altSnapshot = await getDocs(byAltNameQuery);
            results.push(...altSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          } catch (err) {
            devError('ERROR querying cohorts by cohortName:', err);
          }
        }

        const deduped = new Map();
        results.forEach((cohort) => {
          deduped.set(cohort.id, cohort);
        });
        return Array.from(deduped.values());
      };

      let cohorts = await loadCohortsByIds(assignedCohortIds);
      devLog('Cohorts loaded by ID:', cohorts.length, cohorts.map(c => c.name || c.id));

      if (cohorts.length === 0 && userProfile.assignedPrograms?.length > 0) {
        devLog('No cohorts found by ID, trying by program...');
        cohorts = await loadCohortsByPrograms(userProfile.assignedPrograms);
        devLog('Cohorts loaded by program:', cohorts.length);
      }

      const visibleCohorts = cohorts;
      devLog('Visible cohorts after filter:', visibleCohorts.length);

      setAllowedCohorts(visibleCohorts);
      const cohortNames = {};
      visibleCohorts.forEach((cohort) => {
        cohortNames[cohort.id] = cohort.name || cohort.cohortName || cohort.id;
      });
      setCohortNameMap(cohortNames);

      const programIds = Array.from(
        new Set(
          [
            ...visibleCohorts.map(cohort => cohort.programId).filter(Boolean),
            ...(userProfile.assignedPrograms || [])
          ]
        )
      );
      devLog('Program IDs to query:', programIds);

      if (programIds.length > 0) {
        const programmesRef = collection(db, 'programs');
        const chunks = [];
        for (let i = 0; i < programIds.length; i += 10) {
          chunks.push(programIds.slice(i, i + 10));
        }
        const programsList = [];
        for (const chunk of chunks) {
          devLog('Querying programs chunk:', chunk);
          try {
            const progQuery = query(programmesRef, where('__name__', 'in', chunk));
            const progSnapshot = await getDocs(progQuery);
            devLog('Programs query success, found:', progSnapshot.docs.length);
            programsList.push(...progSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          } catch (err) {
            devError('ERROR querying programs:', err);
            throw err;
          }
        }
        setAssignedPrograms(programsList);
      } else {
        devLog('No program IDs, skipping programs query');
        setAssignedPrograms([]);
      }

      const visibleCohortIds = visibleCohorts.map(cohort => cohort.id);
      const visibleCohortNames = new Set(
        visibleCohorts
          .map(cohort => normalizeCohortName(cohort.name || cohort.cohortName || ''))
          .filter(Boolean)
      );
      const visibleCohortAliases = new Set(
        Array.from(visibleCohortNames)
          .map((name) => normalizeCohortMatch(name))
          .filter(Boolean)
      );
      devLog('Will query learners for cohort IDs:', visibleCohortIds);

      let loadedLearners = [];
      if (visibleCohortIds.length > 0) {
        const learnersRef = collection(db, 'users');
        const chunks = [];
        for (let i = 0; i < visibleCohortIds.length; i += 10) {
          chunks.push(visibleCohortIds.slice(i, i + 10));
        }
        for (const chunk of chunks) {
          devLog('Querying learners for cohort chunk:', chunk);
          try {
            const learnerQuery = query(
              learnersRef,
              where('role', '==', 'learner'),
              where('cohortId', 'in', chunk)
            );
            const learnerSnapshot = await getDocs(learnerQuery);
            devLog('Learners query success, found:', learnerSnapshot.docs.length);
            loadedLearners.push(...learnerSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          } catch (err) {
            devError('ERROR querying learners:', err);
            throw err;
          }
        }
      } else {
        if (programIds.length > 0) {
          devLog('No visible cohort IDs, falling back to program-based learner query');
          const learnersRef = collection(db, 'users');
          const chunks = [];
          for (let i = 0; i < programIds.length; i += 10) {
            chunks.push(programIds.slice(i, i + 10));
          }
          for (const chunk of chunks) {
            const learnerQuery = query(
              learnersRef,
              where('role', '==', 'learner'),
              where('programId', 'in', chunk)
            );
            const learnerSnapshot = await getDocs(learnerQuery);
            loadedLearners.push(...learnerSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          }
        } else {
          devLog('No visible cohort IDs, skipping learners query');
        }
      }
      setLearners(loadedLearners);
      devLog('Total learners loaded:', loadedLearners.length);

      // Load resources for assigned cohorts only (not system-wide public resources)
      devLog('Loading resources...');
      try {
        const resourcesRef = collection(db, 'resources');
        const resourcesSnapshot = await getDocs(resourcesRef);
        const allResources = resourcesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // External viewers should only see resources assigned to their allowed cohorts.
        const filteredResources = allResources.filter(resource => {
          const cohortIds = Array.isArray(resource.cohortIds)
            ? resource.cohortIds
            : (resource.cohortId ? [resource.cohortId] : []);

          if (cohortIds.length === 0) return false;
          return cohortIds.some(cId => visibleCohortIds.includes(cId));
        });

        setResources(filteredResources);
        devLog('Resources loaded:', filteredResources.length);
      } catch (err) {
        devError('Error loading resources:', err);
        // Don't fail the whole load if resources fail
      }

      // Load upcoming sessions/meetings for assigned cohorts
      devLog('Loading upcoming sessions...');
      try {
        const meetingsRef = collection(db, 'meetings');
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const meetingsQuery = query(
          meetingsRef,
          where('scheduledDateTime', '>=', Timestamp.fromDate(now)),
          orderBy('scheduledDateTime', 'asc')
        );
        const meetingsSnapshot = await getDocs(meetingsQuery);
        const parseMeetingDate = (value) => {
          if (!value) return null;
          if (value.toDate) return value.toDate();
          if (value.seconds) return new Date(value.seconds * 1000);
          const parsed = new Date(value);
          if (Number.isNaN(parsed.getTime())) return null;
          return parsed;
        };

        const addDays = (date, days) => {
          const next = new Date(date);
          next.setDate(next.getDate() + days);
          return next;
        };

        const getRecurrenceDays = (meeting) => {
          const days = Array.isArray(meeting.recurrenceDays) ? meeting.recurrenceDays : [];
          if (days.length > 0) return days;
          return [1, 2, 3, 4, 5];
        };

        const buildOccurrences = (meeting, rangeStart, rangeEnd) => {
          const occurrences = [];
          const startDate = meeting.scheduledDateTime;
          if (!startDate) return occurrences;

          const recurrenceEnd = meeting.recurrenceEndDate || null;
          const maxEnd = recurrenceEnd && recurrenceEnd > rangeEnd ? rangeEnd : recurrenceEnd || rangeEnd;
          const windowEnd = maxEnd < rangeStart ? rangeStart : maxEnd;

          const current = new Date(startDate);
          current.setSeconds(0, 0);
          if (current < rangeStart) {
            const daysDiff = Math.ceil((rangeStart - current) / (24 * 60 * 60 * 1000));
            current.setDate(current.getDate() + daysDiff);
          }

          const recurrenceDays = getRecurrenceDays(meeting);
          while (current <= windowEnd) {
            if (recurrenceDays.includes(current.getDay())) {
              occurrences.push({
                ...meeting,
                id: `${meeting.id}_${current.toISOString().split('T')[0]}`,
                scheduledDateTime: new Date(current),
                baseMeetingId: meeting.id,
                baseMeeting: meeting
              });
            }
            current.setDate(current.getDate() + 1);
          }

          return occurrences;
        };

        const allMeetings = meetingsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            scheduledDateTime: parseMeetingDate(data.scheduledDateTime),
            recurrenceEndDate: parseMeetingDate(data.recurrenceEndDate)
          };
        });

        const rangeEnd = addDays(now, 30);
        const allUpcomingMeetings = allMeetings.flatMap(meeting => {
          if (meeting.isRecurring) {
            return buildOccurrences(meeting, now, rangeEnd);
          }
          return [meeting];
        }).filter(meeting => meeting.scheduledDateTime && meeting.scheduledDateTime >= now);

        // Filter meetings for viewer's cohorts or direct assignment
        const filteredMeetings = allUpcomingMeetings.filter(meeting => {
          if (meeting.assignedUserIds && meeting.assignedUserIds.includes(currentUser?.uid)) return true;
          const assignedEmails = Array.isArray(meeting.assignedUserEmails)
            ? meeting.assignedUserEmails.map(email => email.toLowerCase())
            : [];
          if (currentUser?.email && assignedEmails.includes(currentUser.email.toLowerCase())) return true;

          const meetingCohortIds = Array.isArray(meeting.cohortIds)
            ? meeting.cohortIds
            : (meeting.cohortId ? [meeting.cohortId] : []);
          const matchesById = meetingCohortIds.some(id => visibleCohortIds.includes(id));
          if (matchesById) return true;

          const nameCandidates = [];
          if (meeting.cohortName) nameCandidates.push(meeting.cohortName);
          if (meeting.title) nameCandidates.push(meeting.title);
          meetingCohortIds.forEach((idOrName) => {
            if (!visibleCohortIds.includes(idOrName)) {
              nameCandidates.push(idOrName);
            }
          });
          const matchesByName = nameCandidates
            .map((name) => normalizeCohortMatch(name))
            .some((name) => name && visibleCohortAliases.has(name));
          if (matchesByName) return true;

          return false;
        });

        const sessionRef = collection(db, 'sessions');
        let sessionSnapshots = [];
        try {
          const collected = new Map();
          const addSnapshot = (snapshot) => {
            snapshot.docs.forEach((docSnap) => {
              collected.set(docSnap.id, docSnap);
            });
          };

          if (visibleCohortIds.length > 0) {
            const chunks = [];
            for (let i = 0; i < visibleCohortIds.length; i += 10) {
              chunks.push(visibleCohortIds.slice(i, i + 10));
            }

            for (const chunk of chunks) {
              try {
                const byCohortQuery = query(
                  sessionRef,
                  where('cohortId', 'in', chunk),
                  where('scheduledDate', '>=', Timestamp.fromDate(now)),
                  orderBy('scheduledDate', 'asc')
                );
                const byCohortSnapshot = await getDocs(byCohortQuery);
                addSnapshot(byCohortSnapshot);
              } catch (err) {
                devError('Error loading sessions by cohortId:', err);
              }

              try {
                const byCohortIdsQuery = query(
                  sessionRef,
                  where('cohortIds', 'array-contains-any', chunk),
                  where('scheduledDate', '>=', Timestamp.fromDate(now)),
                  orderBy('scheduledDate', 'asc')
                );
                const byCohortIdsSnapshot = await getDocs(byCohortIdsQuery);
                addSnapshot(byCohortIdsSnapshot);
              } catch (err) {
                devError('Error loading sessions by cohortIds:', err);
              }
            }
          } else {
            const sessionsQuery = query(
              sessionRef,
              where('scheduledDate', '>=', Timestamp.fromDate(now)),
              orderBy('scheduledDate', 'asc')
            );
            const sessionsSnapshot = await getDocs(sessionsQuery);
            addSnapshot(sessionsSnapshot);
          }

          sessionSnapshots = Array.from(collected.values());
        } catch (error) {
          devError('Error loading sessions fallback:', error);
        }

        const sessionRows = sessionSnapshots.map((docSnap) => {
          const data = docSnap.data();
          const scheduledDateTime = data.scheduledDate?.toDate?.() || null;
          if (!scheduledDateTime) return null;

          const cohortId = data.cohortId || null;
          const cohortIds = Array.isArray(data.cohortIds) ? data.cohortIds : [];
          const singleCohortId = cohortId || (cohortIds.length === 1 ? cohortIds[0] : null);

          return {
            id: `session_${docSnap.id}`,
            title: data.title || data.moduleName || 'Session',
            description: data.description || '',
            scheduledDateTime,
            duration: data.durationMinutes || data.duration || null,
            cohortId,
            cohortIds,
            cohortName: singleCohortId ? cohortNameMap[singleCohortId] : null,
            programId: data.programId || null,
            teamsLink: data.teamsLink || data.meetingLink || null
          };
        }).filter(Boolean);

        const filteredSessions = sessionRows.filter((session) => {
          if (session.cohortId && visibleCohortIds.includes(session.cohortId)) return true;
          if (session.cohortIds.length && session.cohortIds.some(id => visibleCohortIds.includes(id))) return true;
          const sessionName = normalizeCohortMatch(session.title || '');
          if (sessionName && visibleCohortAliases.has(sessionName)) return true;
          return false;
        });

        const meetingKey = (item) => {
          const dateKey = item.scheduledDateTime
            ? new Date(item.scheduledDateTime).toISOString().slice(0, 16)
            : 'unknown';
          const cohortKey = item.cohortId
            || (Array.isArray(item.cohortIds) && item.cohortIds.length ? item.cohortIds.join(',') : '')
            || normalizeCohortMatch(item.cohortName || '');
          const titleKey = normalizeCohortMatch(item.title || '');
          return `${titleKey}|${cohortKey}|${dateKey}`;
        };

        const meetingKeys = new Set(filteredMeetings.map((meeting) => meetingKey(meeting)));
        const combinedMeetings = [
          ...filteredMeetings,
          ...filteredSessions.filter((session) => !meetingKeys.has(meetingKey(session)))
        ];

        const nowTime = new Date();
        const sortedMeetings = combinedMeetings
          .map((meeting) => {
            const start = meeting.scheduledDateTime;
            const durationMinutes = Number(meeting.duration || 0);
            const end = start && durationMinutes ? new Date(start.getTime() + durationMinutes * 60000) : null;
            const isLive = start && end ? nowTime >= start && nowTime <= end : false;
            return { ...meeting, isLive };
          })
          .sort((a, b) => {
            if (a.isLive !== b.isLive) return a.isLive ? -1 : 1;
            const aTime = a.scheduledDateTime ? a.scheduledDateTime.getTime() : 0;
            const bTime = b.scheduledDateTime ? b.scheduledDateTime.getTime() : 0;
            return aTime - bTime;
          });

        setUpcomingSessions(sortedMeetings);
        devLog('Upcoming sessions loaded:', filteredMeetings.length);
      } catch (err) {
        devError('Error loading upcoming sessions:', err);
        // Don't fail the whole load if sessions fail
      }

      devLog('=== EXTERNAL VIEWER LOAD DATA SUCCESS ===');
    } catch (error) {
      devError('Error loading data:', error);
      devError('Error code:', error.code);
      devError('Error message:', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const getStats = () => {
    const loggedInCount = learners.filter(l => l.lastLoginAt || l.hasLoggedIn).length;
    const neverLoggedInCount = learners.filter(l => !l.lastLoginAt && !l.hasLoggedIn).length;
    const activeCount = learners.filter(l => l.isActive !== false).length;

    return {
      totalLearners: learners.length,
      loggedIn: loggedInCount,
      neverLoggedIn: neverLoggedInCount,
      active: activeCount
    };
  };

  // Format date for display
  const formatDate = (dateValue) => {
    if (!dateValue) return 'Never';
    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    if (isNaN(date.getTime())) return 'Invalid';
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format session date/time for display
  const formatSessionDate = (dateValue) => {
    if (!dateValue) return 'TBD';
    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    if (isNaN(date.getTime())) return 'Invalid';
    return date.toLocaleDateString('en-ZA', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSessionCohortLabels = (session) => {
    const labels = [];
    if (session.cohortId && cohortNameMap[session.cohortId]) {
      labels.push(cohortNameMap[session.cohortId]);
    }

    const cohortIds = Array.isArray(session.cohortIds) ? session.cohortIds : [];
    cohortIds.forEach((id) => {
      if (cohortNameMap[id] && !labels.includes(cohortNameMap[id])) {
        labels.push(cohortNameMap[id]);
      }
    });

    if (session.cohortName && !labels.includes(session.cohortName)) {
      labels.push(session.cohortName);
    }

    return labels;
  };

  // Get resource type icon
  const getResourceIcon = (type) => {
    const icons = {
      pdf: '📄',
      video: '🎬',
      audio: '🎧',
      image: '🖼️',
      document: '📝',
      spreadsheet: '📊',
      presentation: '📽️',
      link: '🔗',
      tool: '🛠️',
      template: '📋'
    };
    return icons[type] || '📁';
  };

  // Get category label
  const getCategoryLabel = (category) => {
    const labels = {
      documents: 'Documents & PDFs',
      videos: 'Videos & Tutorials',
      links: 'External Links',
      templates: 'Templates & Forms',
      tools: 'Tools & Software',
      assessments: 'Assessments',
      presentations: 'Presentations',
      research: 'Research & Articles'
    };
    return labels[category] || category;
  };

  // Export learner list
  const exportLearnerList = () => {
    const programNameMap = Object.fromEntries(assignedPrograms.map(p => [p.id, p.name || p.programName]));
    const data = learners.map(learner => ({
      'Name': learner.displayName || 'N/A',
      'Email': learner.email || 'N/A',
      'ID Number': learner.idNumber || 'N/A',
      'Cohort': cohortNameMap[learner.cohortId] || learner.cohortName || 'Unknown Cohort',
      'Programme': programNameMap[learner.programId] || learner.programName || 'Unknown Programme',
      'Last Login': formatDate(learner.lastLoginAt),
      'Login Status': (learner.lastLoginAt || learner.hasLoggedIn) ? 'Has Logged In' : 'Never Logged In',
      'Status': learner.isActive !== false ? 'Active' : 'Inactive'
    }));

    if (data.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `learner-list-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const stats = getStats();

  if (loading) {
    return (
      <>
        <ExternalNavbar />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading dashboard...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <ExternalNavbar />
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">External Viewer Dashboard</h1>
              <p className="mission-text mb-4">
                Access reports and analytics for assigned programmes
              </p>
              <p className="mb-0">
                Review learner trends, progress summaries, and cohort-level insights.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading dashboard...</p>
          </div>
        )}

        {!loading && (
          <>

        {/* Stats Cards */}
        <Row className="mb-4">
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 dashboard-card">
              <Card.Body className="text-center">
                <h3 className="text-primary mb-1">{assignedPrograms.length}</h3>
                <small className="text-muted">Assigned Programmes</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 dashboard-card">
              <Card.Body className="text-center">
                <h3 className="text-info mb-1">{stats.totalLearners}</h3>
                <small className="text-muted">Total Learners</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 dashboard-card">
              <Card.Body className="text-center">
                <h3 className="text-success mb-1">{stats.loggedIn}</h3>
                <small className="text-muted">Have Logged In</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 dashboard-card">
              <Card.Body className="text-center">
                <h3 className="text-danger mb-1">{stats.neverLoggedIn}</h3>
                <small className="text-muted">Never Logged In</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Main Tabs */}
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
          {/* Overview Tab */}
          <Tab eventKey="overview" title="Overview">
            <Row>
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header>
                    <strong>Assigned Programmes</strong>
                    <Badge bg="secondary" className="ms-2">View Only</Badge>
                  </Card.Header>
                  <Card.Body>
                    {assignedPrograms.length === 0 ? (
                      <Alert variant="warning" className="mb-0">
                        No programmes assigned yet. Contact the administrator.
                      </Alert>
                    ) : (
                      <Table striped hover size="sm">
                        <thead>
                          <tr>
                            <th>Programme Name</th>
                            <th>Status</th>
                            <th>Learners</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignedPrograms.map(prog => (
                            <tr key={prog.id}>
                              <td>{prog.name || prog.programName || 'Unknown Programme'}</td>
                              <td>
                                <Badge bg={prog.status === 'active' ? 'success' : 'secondary'}>
                                  {prog.status || 'active'}
                                </Badge>
                              </td>
                              <td>
                                {learners.filter(l => l.programId === prog.id).length}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header>
                    <strong>Login Status Summary</strong>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Logged In</span>
                        <span>{stats.loggedIn} / {stats.totalLearners}</span>
                      </div>
                      <ProgressBar
                        now={stats.totalLearners > 0 ? (stats.loggedIn / stats.totalLearners) * 100 : 0}
                        variant="success"
                      />
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Never Logged In</span>
                        <span>{stats.neverLoggedIn} / {stats.totalLearners}</span>
                      </div>
                      <ProgressBar
                        now={stats.totalLearners > 0 ? (stats.neverLoggedIn / stats.totalLearners) * 100 : 0}
                        variant="danger"
                      />
                    </div>
                    <hr />
                    <p className="text-muted small mb-0">
                      {Math.round((stats.loggedIn / (stats.totalLearners || 1)) * 100)}% of learners have signed in to the platform.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Upcoming Sessions Section */}
            <Row>
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>Upcoming Sessions</strong>
                    <Badge bg="info">{upcomingSessions.length} scheduled</Badge>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {upcomingSessions.length === 0 ? (
                      <Alert variant="info" className="mb-0">
                        No upcoming sessions scheduled for your assigned cohorts.
                      </Alert>
                    ) : (
                      <ListGroup variant="flush">
                        {upcomingSessions.map(session => (
                          <ListGroup.Item key={session.id} className="px-0">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <strong>{session.title || 'Untitled Session'}</strong>
                                <div className="text-muted small">
                                  {formatSessionDate(session.scheduledDateTime)}
                                </div>
                                {session.description && (
                                  <div className="text-muted small mt-1" style={{ maxWidth: '300px' }}>
                                    {session.description.length > 100
                                      ? `${session.description.substring(0, 100)}...`
                                      : session.description}
                                  </div>
                                )}
                                <div className="mt-1">
                                  {getSessionCohortLabels(session).map((label) => (
                                    <Badge key={label} bg="outline-secondary" className="me-1 border">
                                      {label}
                                    </Badge>
                                  ))}
                                  {session.duration && (
                                    <Badge bg="light" text="dark" className="border">
                                      {session.duration} min
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {session.teamsLink && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  href={session.teamsLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Join
                                </Button>
                              )}
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Resources Section */}
              <Col lg={6} className="mb-4">
                <Card className="h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>Resources</strong>
                    <Badge bg="primary">{resources.length} available</Badge>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {resources.length === 0 ? (
                      <Alert variant="info" className="mb-0">
                        No resources available for your assigned cohorts yet.
                      </Alert>
                    ) : (
                      <ListGroup variant="flush">
                        {resources.slice(0, 10).map(resource => (
                          <ListGroup.Item key={resource.id} className="px-0">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <div className="d-flex align-items-center">
                                  <span className="me-2">{getResourceIcon(resource.type)}</span>
                                  <strong>{resource.title || 'Untitled Resource'}</strong>
                                </div>
                                {resource.description && (
                                  <div className="text-muted small mt-1" style={{ maxWidth: '300px' }}>
                                    {resource.description.length > 80
                                      ? `${resource.description.substring(0, 80)}...`
                                      : resource.description}
                                  </div>
                                )}
                                <div className="mt-1">
                                  <Badge bg="light" text="dark" className="me-1 border">
                                    {getCategoryLabel(resource.category)}
                                  </Badge>
                                  {resource.accessLevel && (
                                    <Badge
                                      bg={resource.accessLevel === 'public' ? 'success' : 'secondary'}
                                      className="me-1"
                                    >
                                      {resource.accessLevel}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {(resource.url || resource.fileUrl) && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  href={resource.url || resource.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View
                                </Button>
                              )}
                            </div>
                          </ListGroup.Item>
                        ))}
                        {resources.length > 10 && (
                          <ListGroup.Item className="text-center text-muted">
                            +{resources.length - 10} more resources
                          </ListGroup.Item>
                        )}
                      </ListGroup>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          {/* Reports Tab */}
          <Tab eventKey="reports" title="Reports">
            <Card>
              <Card.Header>
                <strong>Progress Reports & Analytics</strong>
              </Card.Header>
              <Card.Body>
                <Alert variant="success" className="mb-3">
                  <strong>Report Access:</strong> You can view and download detailed progress reports
                  for learners in your assigned programmes.
                </Alert>

                {learners.length > 0 ? (
                  <ProgressReportGenerator
                    learners={learners}
                    cohorts={allowedCohorts}
                    programs={assignedPrograms}
                    isExternal={true}
                    useProvidedData={true}
                    reportHistoryUserId={currentUser?.uid || null}
                  />
                ) : (
                  <Alert variant="warning">
                    No learners available for report generation.
                    Please ensure you have learners assigned to your cohorts.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Sales Leaderboard Tab */}
          <Tab eventKey="sales" title="Sales Leaderboard">
            <Row>
              <Col lg={12} className="mb-4">
                <TakeALotLeaderboard />
              </Col>
            </Row>
          </Tab>

          {/* Export Data Tab */}
          <Tab eventKey="export" title="Export Data">
            <Card>
              <Card.Header>
                <strong>Data Export</strong>
              </Card.Header>
              <Card.Body>
                <Alert variant="info" className="mb-3">
                  <strong>Export Capabilities:</strong> You can export learner data and progress metrics
                  for your assigned programmes.
                </Alert>

                <Card className="mb-3">
                  <Card.Header>
                    <strong>Quick Export</strong>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-3">Generate instant exports of key data:</p>
                    <Row>
                      <Col md={6} className="mb-3">
                        <Button
                          variant="outline-primary"
                          className="w-100"
                          onClick={exportLearnerList}
                        >
                          Export Learner List (CSV)
                        </Button>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Button variant="outline-secondary" className="w-100" disabled>
                          Export Attendance Summary (Coming Soon)
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Card.Body>
            </Card>
          </Tab>

          {/* Learners Tab */}
          <Tab eventKey="learners" title="Learners">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Learner Information</strong>
                  <Badge bg="secondary" className="ms-2">Read Only</Badge>
                </div>
                <Button size="sm" variant="outline-primary" onClick={exportLearnerList}>
                  Export CSV
                </Button>
              </Card.Header>
              <Card.Body>
                {learners.length === 0 ? (
                  <Alert variant="warning" className="mb-0">
                    No learners found in your assigned cohorts.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover size="sm">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Cohort</th>
                          <th>Last Login</th>
                          <th>Login Status</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {learners.map(learner => (
                          <tr key={learner.id}>
                            <td>{learner.displayName || learner.name || 'N/A'}</td>
                            <td>{learner.email}</td>
                            <td>{cohortNameMap[learner.cohortId] || learner.cohortName || 'Unknown Cohort'}</td>
                            <td>{formatDate(learner.lastLoginAt)}</td>
                            <td>
                              <Badge bg={(learner.lastLoginAt || learner.hasLoggedIn) ? 'success' : 'danger'}>
                                {(learner.lastLoginAt || learner.hasLoggedIn) ? 'Logged In' : 'Never'}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={learner.isActive !== false ? 'success' : 'secondary'}>
                                {learner.isActive !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
          </>
        )}
      </Container>
    </>
  );
};

export default ExternalViewerDashboard;
